import { useContext } from 'react'
import { useMutation } from 'react-query'
import { apiPost, apiPatch, apiDelete } from 'api'
import { baseUrl } from 'api/helpers'
import axios from 'axios'
import AuthContext from 'context/auth-context'

const createDashboardColumn = async (token, params) => {
  const objectName = 'dashboard_column'
  const url = `dashboards/${params.dashboard_id}/dashboard_columns`

  const data = await apiPost({ url, token, objectName, params })
  return data
}

export function useCreateDashboardColumn({ params, options }) {
  const token = useContext(AuthContext)

  return useMutation(
    (mutationParams) => {
      const finalParams = mutationParams || params
      return createDashboardColumn(token, finalParams, finalParams.dashboard_id)
    },
    { ...options }
  )
}

const updateDashboardColumn = async (token, params, id) => {
  const objectName = 'dashboard_column'
  const url = `dashboard_columns/${id}`

  const data = await apiPatch({ url, token, objectName, params })
  return data
}

export function useUpdateDashboardColumns({ params, options }) {
  const token = useContext(AuthContext)

  return useMutation(
    () => {
      return updateDashboardColumn(token, params, params.dashboard_column_id)
    },
    { ...options }
  )
}

const deleteDashboardColumn = async (token, id) => {
  // Support both single delete and batch delete with ids array
  if (Array.isArray(id)) {
    // For batch delete, use any valid ID in the URL and pass all IDs as params
    const fullUrl = `${baseUrl}/dashboard_columns/${id[0]}`
    
    const data = await axios.delete(fullUrl, {
      params: { ids: id },
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    })
    return data.data
  } else {
    // Single delete - just use the ID in the URL
    const url = `dashboard_columns/${id}`
    const data = await apiDelete({ url, token })
    return data
  }
}

export function useDeleteDashboardColumn({ id, options }) {
  const token = useContext(AuthContext)

  return useMutation(
    (mutationId) => {
      const finalId = mutationId !== undefined ? mutationId : id
      return deleteDashboardColumn(token, finalId)
    },
    { ...options }
  )
}

const reorderDashboardColumns = async (token, dashboardId, columnOrders) => {
  const fullUrl = `${baseUrl}/dashboards/${dashboardId}/column_orders`
  const payload = { column_orders: columnOrders }

  const data = await axios.patch(fullUrl, payload, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  })
  return data.data
}

/**
 * Hook for reordering dashboard columns
 * @param {string} dashboardId - Dashboard ID
 * @returns {Object} Mutation object with mutate function
 */
export function useReorderDashboardColumns(dashboardId) {
  const token = useContext(AuthContext)

  return useMutation((columnOrders) => {
    return reorderDashboardColumns(token, dashboardId, columnOrders)
  })
}

/**
 * Utility function to convert column order array to API format
 * @param {Array} columnOrder - Array of column database names in order
 * @param {Array} dashboardColumns - Array of dashboard column objects
 * @returns {Array} Array formatted for API: [{ id: uuid, column_order: 0 }, ...]
 */
export function formatColumnOrderForAPI(columnOrder, dashboardColumns) {
  if (!columnOrder || !Array.isArray(columnOrder)) {
    console.warn('Invalid columnOrder:', columnOrder)
    return []
  }

  if (!dashboardColumns || !Array.isArray(dashboardColumns)) {
    console.warn('Invalid dashboardColumns:', dashboardColumns)
    return []
  }

  // Create lookup map by database column name
  const columnsByDbName = {}
  dashboardColumns.forEach((dc) => {
    if (dc.column_name && dc.dashboard_column_id) {
      columnsByDbName[dc.column_name] = dc
    }
  })

  // Map the ordered column names to dashboard column objects with order
  const result = columnOrder
    .map((columnName, index) => {
      const dashboardColumn = columnsByDbName[columnName]
      if (!dashboardColumn) {
        console.warn(`Dashboard column not found for: ${columnName}`)
        return null
      }

      return {
        id: dashboardColumn.dashboard_column_id,
        column_order: index
      }
    })
    .filter(Boolean) // Remove null entries

  return result
}
